import React, { useEffect, useState } from 'react';
import { MutateFormFields, ModalMutateCatalogProps } from './types';
import { Button } from '@vtbhome/juicyfront';
import { Body, Footer, InputGroup } from './modal-mutate-catalog.styles';
import { Field, Form } from 'react-final-form';
import { validate } from '../../lib/form-validate';
import { Typography } from '@shared/ui/typography/typography';
import { InputText } from '@shared/ui/input-text/input-text';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useCreateCatalogMutation, useUpdateCatalogMutation } from '@entities/catalog/api/catalog-api';
import { CACHE_KEY_CATALOG_CREATE, CACHE_KEY_CATALOG_UPDATE } from '@entities/catalog/config/api-constants';
import { useCatalogInfoSharedQuery } from '@entities/catalog/libs/hooks/use-catalogs-short-info-shared-query/use-catalogs-short-info-shared-query';
import { useAdminProductInfinityListQuery } from '@entities/product/api/product-api';
import { ProductsListDataRequest } from '@entities/product/api/product-types';
import { Select } from '@shared/ui/select/select';
import { OrganizationsListDataRequest } from '@entities/organization/api/organization-types';
import { useOrganizationsInfinityListQuery } from '@entities/organization/api/organization-api';
import { DEFAULT_ADMIN_PRODUCTS_LIST_PARAMETERS } from '@entities/product/config/store-constants';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { EditFormKeys } from '../../config/form-constants';
import { useNormalizeInitialValues } from '../../lib/normalize-initial-values';
import { normalizeSubmitValues } from '../../lib/normalize-submit-values';
import { useProductSelect } from '../../lib/hooks/use-product-options/use-product-options';
import { useOrganizationSelect } from '../../lib/hooks/use-organization-options/use-organization-options';
import { Modal } from '@shared/ui/modal/modal';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';

export const ModalMutateCatalog: React.FC<ModalMutateCatalogProps> = ({
    catalogId,
    isShow,
    action = 'create',
    onClose,
    onAccept,
}) => {
    /* Products */
    const [productsListParameters, setProductsListParameters] = useState<ProductsListDataRequest>({
        ...DEFAULT_ADMIN_PRODUCTS_LIST_PARAMETERS,
        take: 10,
    });
    const {
        data: productsList,
        isLoading: isProductsListLoading,
        isFetching: isProductsListFetching,
    } = useAdminProductInfinityListQuery(productsListParameters);

    const {
        productOptions,
        productEmptyOption,
        isShowEmptyLoaderProducts,
        infinityScrollProductsProps,
        onSearchProducts,
    } = useProductSelect({
        productsData: productsList,
        isLoading: isProductsListLoading,
        isFetching: isProductsListFetching,
        setProductsListParameters,
    });

    /* Organizations */
    const [organizationsListParameters, setOrganizationsListParameters] = useState<OrganizationsListDataRequest>({
        take: 10,
    });
    const {
        data: organizationsList,
        isLoading: isOrganizationsLoading,
        isFetching: isOrganizationsFetching,
    } = useOrganizationsInfinityListQuery(organizationsListParameters);
    const {
        organizationOptions,
        organizationEmptyOption,
        isShowEmptyLoaderOrganizations,
        infinityScrollOrganizationsProps,
        onSearchOrganizations,
    } = useOrganizationSelect({
        organizationsData: organizationsList,
        isLoading: isOrganizationsLoading,
        isFetching: isOrganizationsFetching,
        setOrganizationsListParameters,
    });

    /* Catalog */
    const [getCatalogInfo, getCatalogInfoResult] = useCatalogInfoSharedQuery(catalogId);
    const [createCatalog, createCatalogResult] = useCreateCatalogMutation({
        fixedCacheKey: CACHE_KEY_CATALOG_CREATE,
    });
    const [editCatalog, editCatalogResult] = useUpdateCatalogMutation({
        fixedCacheKey: CACHE_KEY_CATALOG_UPDATE,
    });
    const catalogInfo = getCatalogInfoResult.data;
    const error = getQueryError(getCatalogInfoResult);

    const isCreateAction = action === 'create';
    const isLoadingInfo =
        getCatalogInfoResult.isFetching ||
        getCatalogInfoResult.isLoading ||
        (getCatalogInfoResult.isUninitialized && !isCreateAction);
    const isLoadingMutate = editCatalogResult.isLoading || createCatalogResult.isLoading;

    const initialValues = useNormalizeInitialValues(catalogInfo);
    const formRef = useResetModalForm<MutateFormFields, typeof initialValues>(isShow);

    const handleSubmit = async (fields: MutateFormFields) => {
        let isError = false;
        if (catalogId && action === 'edit') {
            const result = await editCatalog({
                id: catalogId,
                ...normalizeSubmitValues(fields),
            });
            getCatalogInfo(catalogId, false);
            isError = Boolean(result.error);
        } else if (isCreateAction) {
            const result = await createCatalog(normalizeSubmitValues(fields));
            isError = Boolean(result.error);
        }
        if (!isError) {
            onAccept();
        }
    };

    useEffect(() => {
        if (isShow && catalogId) {
            getCatalogInfo(catalogId);
        }
    }, [isShow, catalogId]);

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validate}
            initialValues={initialValues}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        variant='drawer'
                        size='l'
                        header={
                            <Typography size='h6'>{`${isCreateAction ? 'Создание' : 'Редактирование'} каталога`}</Typography>
                        }
                        footer={
                            <Footer>
                                <Button disabled={isLoadingMutate} buttonType='light' onClick={onClose}>
                                    Отменить
                                </Button>
                                <Button
                                    preloader={isLoadingMutate}
                                    disabled={isLoadingMutate}
                                    buttonType='primary'
                                    onClick={form.handleSubmit}>
                                    Сохранить
                                </Button>
                            </Footer>
                        }
                        onClose={onClose}>
                        <Body>
                            <NetworkStateNew
                                size='s'
                                isLoading={isLoadingInfo}
                                isError={Boolean(error)}
                                isInitialized={!isLoadingInfo}
                                isFiltered={false}
                                hasData={isCreateAction || Boolean(catalogInfo)}
                                errorProps={{
                                    title: 'Ошибка загрузки каталогов',
                                    description: error?.message,
                                }}>
                                <InputGroup>
                                    <Field
                                        name={EditFormKeys.Name}
                                        render={({ input, meta }) => (
                                            <InputText
                                                label='Название каталога'
                                                placeholder='Введите название каталога'
                                                required
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Description}
                                        render={({ input, meta }) => (
                                            <InputText
                                                label='Описание'
                                                placeholder='Введите описание каталога'
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Code}
                                        render={({ input, meta }) => (
                                            <InputText
                                                label='Системное имя'
                                                placeholder='Введите системное имя'
                                                required
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Products}
                                        render={({ input, meta }) => {
                                            return (
                                                <Select
                                                    isAsync
                                                    clearable
                                                    multiselect
                                                    label='Прикрепление продуктов'
                                                    placeholder='Выберите продукты из списка'
                                                    isShowPreloaderInList={isShowEmptyLoaderProducts}
                                                    onSearch={onSearchProducts}
                                                    infinityScrollProps={infinityScrollProductsProps}
                                                    values={input.value || productEmptyOption}
                                                    options={productOptions}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            );
                                        }}
                                    />
                                    <Field
                                        name={EditFormKeys.Companies}
                                        render={({ input, meta }) => {
                                            return (
                                                <Select
                                                    isAsync
                                                    clearable
                                                    multiselect
                                                    label='Пренадлежность к организациям'
                                                    placeholder='Выберите организации из списка'
                                                    isShowPreloaderInList={isShowEmptyLoaderOrganizations}
                                                    onSearch={onSearchOrganizations}
                                                    infinityScrollProps={infinityScrollOrganizationsProps}
                                                    values={input.value || organizationEmptyOption}
                                                    options={organizationOptions}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            );
                                        }}
                                    />
                                </InputGroup>
                            </NetworkStateNew>
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
